/**
 * Arbre de skills : **achat d'un nœud**, **respec**, et **agrégation des assists possédés**
 * — SPEC-MOTEUR §2.2 (lignes `acheter_skill` / `respec`) et §9.3, SPEC-CONTENU §3.2 (`Skill`,
 * `TIER_COST`, `prerequis`) et §2.2 (assists), avenant **A-1** point 5.
 *
 * Ce module ne calcule **aucun effet** par lui-même : les effets d'un nœud vivent dans
 * `content/skills.json` et sont lus par `engine/effects.ts`.
 *
 *  - **à l'achat** : `applyEffects(..., "skill", ...)` applique les effets *ponctuels* du nœud
 *    et date ses *temporaires* ; les persistants permanents ne sont PAS recopiés dans l'état
 *    (piège documenté d'`effects.ts`) — ils se relisent depuis le contenu via `state.skills`.
 *    C'est exactement ce qui rend le respec propre : vider `state.skills` suffit à annuler
 *    tout l'arbre, sans inventaire à défaire.
 *  - **plus tard** : `collectModifiers(state, content)` agrège potentiels, assists, unlocks…
 *    Ce module n'en duplique rien ; il n'expose que des accès nommés (§ « assists agrégés »).
 *
 * Arbitrages appliqués :
 *  - **A6** — respec : 250 ₵ **doublés** à chaque respec (250/500/1000…), rend **100 %** des
 *    PC investis, vide `skills`. Le coût est déjà dans `actionCost` (`actions.ts`) : ce module
 *    ne le recode pas, il le lit (`coutRespec`).
 *  - **A21** — les potentiels viennent des `potential_add` **sommés** du contenu (bases
 *    Qualité 0,8 / Diffusion 1,0 / Portée 0,10), pas du `0,8 + 0,1 × nb de nœuds` de
 *    SPEC-MOTEUR §4.3. L'agrégation est celle de `collectModifiers`.
 *  - **A25** — le contenu arrive **en paramètre**, jamais lu depuis un fichier.
 *  - **⟦A-1⟧ point 5** — pendant la tranche 0 → 1 000 abonnés (`flags.grand_saut_done` faux),
 *    la part en Clicks des nœuds de **tier 1-2** est **supprimée** (« la débrouille ») ; tous
 *    les coûts € s'appliquent après le Grand Saut. Voir `coutEurosDuNoeud`.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucun `Math.random`, aucune horloge système, aucun tirage
 * (l'achat et le respec sont entièrement déterministes — aucun `RngPurpose` n'est consommé).
 */
import { ASSIST_COMPAT, TIER_COST } from "../content/schema.js";
import { SYSTEMES_TOUS_ACTIFS, systemes } from "./menage.js";
import { noterDepenseProduction } from "./axe-produit.js";
import { argentOuvert } from "./escalier.js";
import { porteAbonnes, porteFermee } from "./producteur.js";
import { applyEffectsInPlace, collectModifiers, } from "./effects.js";
import { InvalidAction, } from "./types.js";
/**
 * Extrait les skills d'un contenu moteur qui les porte. `EngineContent` ne déclare pas
 * encore `skills` (voir `BuyableSkill`) : la lecture est donc **élargie**, jamais castée en
 * `any`. Contenu sans skills ⇒ arbre vide, et tout achat échoue en `skill_unknown` — l'absence
 * est visible dans les tests plutôt que silencieusement permissive.
 */
export function contenuSkills(content) {
    const porteur = content;
    return { skills: porteur.skills ?? [] };
}
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/** Respec verrouillé sous ce nombre d'abonnés (PLAN §4.12, SPEC-MOTEUR §2.2). */
export const SEUIL_RESPEC = 10_000;
/**
 * **⟦A-1⟧ point 5** : tier maximal dont la part en Clicks est supprimée avant le Grand Saut.
 * Tier 1-2 = le matériel de débrouille (webcam 50 ₵, ring light 80 ₵, merch 200 ₵) : on se
 * débrouille, on n'achète pas. Au-delà (le_setup 300 ₵, b_roll 400 ₵…), le coût reste dû —
 * et comme l'argent est verrouillé à 0 avant 1 000 abonnés, ces nœuds attendent le Grand
 * Saut. C'est la lecture littérale de l'avenant (« les nœuds tier 1-2 […] voient leur part €
 * supprimée ; tous les coûts € s'appliquent après le Grand Saut »).
 */
export const TIER_MAX_DEBROUILLE = 2;
// ─────────────────────────────────────────────────────────────────────────────
// Lectures : coûts, prérequis
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦C62 · friction 6⟧ « 1 point », « 2 points » : le sigle « PC » se lisait « ordinateur ». Seul le TEXTE du journal change ;
 * `state.pc`, `cout_pc`, `pc_rendus` et `not_enough_pc` restent des noms techniques.
 */
function pointsTexte(n) {
    return `${n} point${Math.abs(n) >= 2 ? "s" : ""}`;
}
/** Le nœud d'id `skillId`, ou `undefined` s'il n'existe pas dans le contenu. */
export function skillParId(content, skillId) {
    return content.skills.find((s) => s.id === skillId);
}
/**
 * Coût en PC d'un nœud — **le `cout_pc` du contenu**, `TIER_COST[tier]` en repli.
 *
 * ⟦A-77, décision de Mathieu du 21/08⟧ **La priorité s'est inversée ici, et c'est l'avenant
 * qui le demande** : `cout_pc` de `le_producteur` est DÉCOUPLÉ de `TIER_COST` (12 → 5), au
 * motif que sa porte est le compteur d'abonnés, pas le prix — T-PROD-12 mesurait un achat à
 * 933 579 abonnés pour une fenêtre voulue de 10 000 à 100 000. L'ancienne écriture
 * (`TIER_COST[tier] ?? cout_pc`) aurait rendu le découplage DÉCORATIF : le contenu aurait
 * affiché 5, le moteur débité 12.
 *
 * Pour les 39 nœuds non découplés, T9 verrouille `cout_pc === TIER_COST[tier]` : les deux
 * écritures rendent le même nombre au caractère près — l'inversion n'a d'effet que là où un
 * avenant l'a décidée.
 */
export function coutPcDuNoeud(skill) {
    return skill.cout_pc ?? TIER_COST[skill.tier];
}
/**
 * Coût en Clicks **effectif** d'un nœud — **⟦A-1⟧ point 5**.
 *
 * Avant le Grand Saut (`flags.grand_saut_done` faux), la part € des nœuds de tier ≤ 2 est
 * supprimée : le joueur « se débrouille » (il vit chez ses parents, il n'a pas d'argent —
 * la jauge est verrouillée à 0). Après le Grand Saut, tous les coûts € s'appliquent.
 */
export function coutEurosDuNoeud(state, skill) {
    if (!state.flags.grand_saut_done && skill.tier <= TIER_MAX_DEBROUILLE)
        return 0;
    return skill.cout_euros;
}
/** Part en Clicks annulée par la débrouille (0 si aucune) — pour le journal et l'UI. */
export function eurosAnnulesParDebrouille(state, skill) {
    return skill.cout_euros - coutEurosDuNoeud(state, skill);
}
/** Prérequis du nœud que le joueur ne possède pas encore (dans l'ordre du contenu). */
export function prerequisManquants(state, skill) {
    const possedes = new Set(state.skills);
    return skill.prerequis.filter((id) => !possedes.has(id));
}
/**
 * Coût du **prochain** respec — arbitrage **A6** : `250 × 2^(respecs déjà faits)`.
 *
 * ⟦C60 bis · A-177⟧ Le prix de base est un réglage du contenu (`systemes.respec_clicks`, 250 par
 * défaut, **0 dans le contenu livré** : « les Clicks ne paient plus que le Marché et l'Équipe », le
 * respec est gratuit). Sans `sys`, la valeur d'avant.
 */
export function coutRespec(state, sys = SYSTEMES_TOUS_ACTIFS) {
    return sys.respec_clicks * Math.pow(2, state.counters.respec_done);
}
/**
 * PC que le respec rendrait : **100 %** des PC investis (somme des `TIER_COST` des nœuds
 * possédés, arbitrage A6). Un id de `state.skills` absent du contenu ne rend rien — il ne
 * peut venir que d'une dérive de contenu, que le boot du serveur refuse déjà.
 */
export function remboursementRespec(state, content) {
    let total = 0;
    for (const id of state.skills) {
        const skill = skillParId(content, id);
        if (skill)
            total += coutPcDuNoeud(skill);
    }
    return total;
}
// ─────────────────────────────────────────────────────────────────────────────
// Validation — une action invalide ne produit AUCUN effet de bord (§2.2)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Raison de refus d'un achat, **sans lever** : c'est ce que l'UI de l'arbre (M2.25) affiche
 * sous un nœud grisé. `null` = achat possible. Ordre des vérifications = celui d'`actions.ts`
 * (structurel d'abord, ressources ensuite) : « il te manque *Webcam* » est plus utile que
 * « tu es fauché », surtout depuis l'avenant A-1 où l'on démarre à 0 ₵.
 */
export function refusAchat(state, skillId, content) {
    const skill = skillParId(content, skillId);
    if (!skill)
        return new InvalidAction("skill_unknown", { skill_id: skillId });
    if (state.skills.includes(skill.id)) {
        return new InvalidAction("skill_already_owned", { skill_id: skill.id });
    }
    const manquants = prerequisManquants(state, skill);
    if (manquants.length > 0) {
        return new InvalidAction("skill_prereq_missing", {
            skill_id: skill.id,
            manquants: manquants.join(","),
            premier_manquant: manquants[0] ?? null,
        });
    }
    // ⟦SPEC-PRODUCTEUR §2.2 / A-66⟧ **La porte d'abonnés**, testée APRÈS les prérequis et AVANT
    // les ressources : le joueur doit lire « il te faut 10 000 abonnés », pas « pas assez de
    // PC ». Amendement partagé avec ⟦A-44⟧ (le guide MeTube, gardé à 3 000).
    //
    // ⟦A-43⟧ Le nœud reste **visible et grisé**, avec sa condition affichée — c'est une promesse,
    // et une promesse se voit. Le jeu dit *que* c'est gardé, jamais *pourquoi* c'est une bonne
    // idée de l'attendre.
    //
    // ⟦A-10⟧ État neutre : 39 nœuds sur 39 n'ont pas de porte, donc ce test rend `false` pour
    // eux et rien ne change. Un refus ne consomme ni `turn` ni tirage (SPEC-MOTEUR §2.2).
    if (porteFermee(state, skill)) {
        return new InvalidAction("skill_locked_subs", {
            skill_id: skill.id,
            requis: porteAbonnes(skill) ?? 0,
            disponible: state.subscribers,
        });
    }
    const pc = coutPcDuNoeud(skill);
    if (state.pc < pc) {
        return new InvalidAction("not_enough_pc", {
            skill_id: skill.id,
            requis: pc,
            disponible: state.pc,
        });
    }
    const euros = coutEurosDuNoeud(state, skill);
    // ⟦A-29⟧ **« Ce qui coûte de l'argent est dans le marché. »** Le garde jumeau de celui de
    // `validateAction` — il faut les deux, parce que la part € d'un nœud **ne passe pas par la
    // table des coûts** (`actionCost` rend `COUT_NUL` pour `acheter_skill`, les Clicks sont
    // débités ici). Un garde unique dans `actions.ts` aurait donc laissé passer précisément le
    // cas le plus atteignable de l'arc 1 : `le_setup` (tier 3, 300 ₵) n'exige que `webcam` puis
    // `ring_light`, deux nœuds gratuits avant le Grand Saut ⟦A-1 pt 5⟧ — un joueur à 300 abonnés
    // pouvait donc s'y cogner, et lisait « pas assez de Clicks » alors qu'il n'a pas de Clicks
    // du tout et n'en aura pas avant 1 000 abonnés.
    //
    // ⟦A-10⟧ État neutre : `state.money` vaut 0 et ne peut pas bouger avant le Grand Saut, donc
    // `not_enough_money` tombait DÉJÀ juste en dessous. Seul le code du refus change, et rien
    // n'en dépend — `simulate.ts` met le nœud de côté sur n'importe quelle `InvalidAction`.
    if (euros > 0 && !argentOuvert(state)) {
        return new InvalidAction("mecanique_verrouillee", {
            mecanique: "argent",
            skill_id: skill.id,
            cout_clicks: euros,
            subscribers: state.subscribers,
        });
    }
    if (euros > 0 && state.money < euros) {
        return new InvalidAction("not_enough_money", {
            skill_id: skill.id,
            requis: euros,
            disponible: state.money,
        });
    }
    return null;
}
/** Idem, mais lève. Rend le nœud validé, pour éviter une seconde recherche. */
export function validateAcheterSkill(state, skillId, content) {
    const refus = refusAchat(state, skillId, content);
    if (refus)
        throw refus;
    // `refusAchat` a déjà garanti l'existence du nœud.
    return skillParId(content, skillId);
}
/**
 * Garde **structurelle** du respec : abonnés ≥ 10 000 (`respec_locked`). Séparée de la garde
 * de ressource à dessein — voir `validateRespec`.
 */
function gardeRespecStructurelle(state) {
    if (state.subscribers < SEUIL_RESPEC) {
        throw new InvalidAction("respec_locked", {
            subscribers: state.subscribers,
            requis: SEUIL_RESPEC,
        });
    }
}
/**
 * Gardes **complètes** du respec, à l'état d'AVANT résolution (§2.2) : abonnés ≥ 10 000
 * (`respec_locked`) puis Clicks suffisants (`not_enough_money`). `validateAction`
 * (`actions.ts`) porte déjà les deux — la première en dur, la seconde via `actionCost` ; on
 * les répète pour que ce module soit utilisable seul (mêmes seuils, mêmes codes).
 *
 * ⚠️ À n'appeler qu'**avant** la file de résolution : au pas 6, le coût a déjà été débité par
 * le pas 3 et `state.money` ne peut plus le payer une seconde fois. C'est pourquoi
 * `appliquerRespec` ne rejoue que la garde structurelle.
 */
export function validateRespec(state, sys = SYSTEMES_TOUS_ACTIFS) {
    gardeRespecStructurelle(state);
    const cout = coutRespec(state, sys);
    if (state.money < cout) {
        throw new InvalidAction("not_enough_money", { requis: cout, disponible: state.money });
    }
}
/**
 * Les gardes de M1.14 que `validateAction` ne peut pas porter (il ne reçoit pas les skills :
 * `EngineContent` n'a pas le champ). À appeler par `resolve` (M1.17) **juste après**
 * `validateAction`, avant tout effet. Actions sans rapport : no-op.
 */
export function validateSkillsAction(state, action, content) {
    if (action.type === "acheter_skill")
        validateAcheterSkill(state, action.skill_id, content);
    else if (action.type === "respec")
        validateRespec(state, systemes(content));
}
/**
 * Applique l'achat d'un nœud : PC et Clicks débités, effets du nœud appliqués, id ajouté à
 * `state.skills`. **MUTE `state`** — appelé depuis la file de résolution, qui travaille déjà
 * sur un clone (§2.3). Valide d'abord : appelé seul, il ne peut pas produire d'achat invalide.
 *
 * C'est bien ici que les deux coûts sont débités : `actionCost` rend `COUT_NUL` pour
 * `acheter_skill` (§2.2, « PC et Clicks du nœud : débités par M1.14 ») parce qu'ils dépendent
 * du contenu.
 *
 * Aucun `EngineEvent` n'est émis : son `kind` est un enum fermé (§1.8) qui n'a pas de cas
 * « nœud acheté », et l'UI lit l'achat dans le diff d'état. Seule une ligne de journal est
 * posée. Écart signalé dans le rapport de M1.14.
 */
export function appliquerAchatSkill(state, skillId, content, log) {
    const skill = validateAcheterSkill(state, skillId, content);
    const pc = coutPcDuNoeud(skill);
    const euros = coutEurosDuNoeud(state, skill);
    const annules = eurosAnnulesParDebrouille(state, skill);
    state.pc -= pc;
    state.money -= euros;
    // Effets du nœud : ponctuels appliqués une fois, temporaires datés. Les persistants
    // permanents ne sont volontairement PAS tracés (`source: "skill"`) — ils se relisent
    // depuis le contenu via `state.skills`, ce qui rend le respec propre.
    const resultat = applyEffectsInPlace(state, skill.effets, "skill", { source_id: skill.id });
    state.skills.push(skill.id);
    if (log) {
        log.push({
            turn: state.turn,
            clock_h: state.clock_h,
            famille: "technique",
            text: annules > 0
                ? `Nœud « ${skill.id} » débloqué pour ${pointsTexte(pc)} : le matériel, tu te débrouilles (${annules} ₵ économisés).`
                : euros > 0
                    ? `Nœud « ${skill.id} » débloqué : ${pointsTexte(pc)} et ${euros} ₵.`
                    : `Nœud « ${skill.id} » débloqué pour ${pointsTexte(pc)}.`,
        });
        log.push(...resultat.log);
    }
    return {
        skill,
        pc_depenses: pc,
        euros_depenses: euros,
        euros_annules_debrouille: annules,
    };
}
/**
 * Applique le respec : rend **100 %** des PC investis, vide `skills`, incrémente
 * `counters.respec_done` (ce qui **double** le prix du suivant — A6). **MUTE `state`**.
 *
 * ⚠️ Le coût en Clicks n'est **pas** débité ici : `actionCost` le porte déjà (`actions.ts`,
 * ligne `respec`) et la file l'a débité au pas 3 — d'où la garde `not_enough_money` portée par
 * `validateRespec` (avant la file) et non par cette fonction, qui verrait un `money` déjà
 * amputé. Hors file, appeler `validateRespec` puis débiter soi-même `coutRespec`.
 *
 * Les modificateurs datés **posés par un nœud retiré** (un `multiplier` temporaire acheté
 * puis respecé) sont purgés : un bonus qu'on ne possède plus ne survit pas au respec. Les
 * effets ponctuels déjà consommés (PC crédités, jauges déplacées) ne sont pas défaits — ils
 * ont eu lieu ; aucun nœud v1 n'en porte.
 */
export function appliquerRespec(state, content, log, sys = SYSTEMES_TOUS_ACTIFS) {
    gardeRespecStructurelle(state);
    const rendus = remboursementRespec(state, content);
    const retires = [...state.skills];
    state.pc += rendus;
    state.skills = [];
    // Purge des lignes datées dont la source est un nœud retiré (voir doc ci-dessus).
    const sidecar = state.content_effects;
    if (sidecar) {
        const retiresSet = new Set(retires);
        sidecar.modifiers = sidecar.modifiers.filter((m) => m.source === undefined || !retiresSet.has(m.source));
    }
    const cout = coutRespec(state, sys);
    state.counters.respec_done += 1;
    // ⟦C60 bis · A-177⟧ Gratuit, le journal ne parle pas de Clicks. Payant (le jeu d'avant), la phrase
    // d'avant, mot pour mot.
    log?.push({
        turn: state.turn,
        clock_h: state.clock_h,
        text: cout > 0
            ? `Respec : ${retires.length} nœud${retires.length > 1 ? "s" : ""} rendu${retires.length > 1 ? "s" : ""}, ${pointsTexte(rendus)} récupéré${rendus >= 2 ? "s" : ""} pour ${cout} ₵. Le prochain coûtera ${cout * 2} ₵.`
            : `Respec : ${retires.length} nœud${retires.length > 1 ? "s" : ""} rendu${retires.length > 1 ? "s" : ""}, ${pointsTexte(rendus)} récupéré${rendus >= 2 ? "s" : ""}.`,
    });
    return { pc_rendus: rendus, noeuds_retires: retires, cout, respec_done: state.counters.respec_done };
}
// ─────────────────────────────────────────────────────────────────────────────
// Le hook de résolution (§2.3 pas 6)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Hook `effetAction` de `applyAction` pour `acheter_skill` et `respec` (`ResolutionHooks`,
 * `actions.ts`). Rend `true` quand l'action a été traitée.
 *
 * Les skills sont lus dans `ctx.content` (`contenuSkills`) : le contenu chargé par
 * `content/load.ts` les porte. Une action invalide lève depuis ici — `applyAction` travaille
 * sur un clone qu'il ne rend jamais, donc l'état d'entrée reste intact (ni PC, ni ₵, ni turn),
 * ce que le test « aucun effet de bord » vérifie.
 */
export function effetActionSkills(ctx, action) {
    if (action.type === "acheter_skill") {
        const achat = appliquerAchatSkill(ctx.state, action.skill_id, contenuSkills(ctx.content), ctx.log);
        // ⟦SPEC-AXE-PRODUIT §3.1⟧ **Le `cout_euros` d'un nœud acheté est une dépense de
        //   production** — premier des trois signaux. La branche Tournage EST l'échelle de
        //   matériel ⟦A-9 §2⟧, et *Le Studio*, *Agent* et *Monteur salarié* sont exactement ce que
        //   « se professionnaliser » veut dire.
        //
        //   `euros_depenses` et non `cout_euros` : c'est ce qui a réellement quitté la poche.
        //   Avant le Grand Saut, la débrouille ⟦A-1 point 5⟧ annule la part € des tiers 1-2 — un
        //   nœud offert n'a rien coûté, donc il ne dit rien de la façon de produire. (L'axe dort
        //   de toute façon jusque-là ; la distinction compte pour un état rechargé.)
        //
        //   Écrit ici plutôt que dans `appliquerAchatSkill` : c'est ce niveau-ci qui tient
        //   `ctx.content`, donc l'interrupteur. Le module d'achat n'a pas à connaître l'axe.
        noterDepenseProduction(ctx.state, ctx.content, achat.euros_depenses);
        return true;
    }
    if (action.type === "respec") {
        appliquerRespec(ctx.state, contenuSkills(ctx.content), ctx.log, systemes(ctx.content));
        return true;
    }
    return false;
}
/** Jeu de hooks de M1.14, à fusionner par M1.17 avec ceux des autres tâches. */
export const HOOKS_SKILLS = { effetAction: effetActionSkills };
/** Tous les assists possédés, les 5 mini-jeux toujours présents (clé stable pour l'UI). */
export function assistsPossedes(state, content) {
    return collectModifiers(state, content).assists;
}
/** Les assists possédés pour UN mini-jeu — l'entrée de `generateAtelierPattern` (M2.8). */
export function assistsDuMiniJeu(state, content, minigame) {
    return assistsPossedes(state, content)[minigame];
}
/**
 * Valeur d'un assist précis, `0` s'il n'est pas possédé. Un couple
 * `(minigame, param)` hors `ASSIST_COMPAT` (SPEC-CONTENU §2.2) rend `0` : le contenu validé
 * n'en produit pas (test T10), mais un appelant peut poser la question.
 *
 * Pour un param booléen (`piste_beat_visible`…), `1` = actif — cf. `assistActif`.
 */
export function valeurAssist(state, content, minigame, param) {
    if (!ASSIST_COMPAT[minigame].includes(param))
        return 0;
    return assistsDuMiniJeu(state, content, minigame)[param] ?? 0;
}
/** Un assist booléen est-il actif ? (`value === 1` pour les params de `ASSIST_BOOL_PARAMS`.) */
export function assistActif(state, content, minigame, param) {
    return valeurAssist(state, content, minigame, param) >= 1;
}
//# sourceMappingURL=skills.js.map